import { 
  collection, 
  getDocs, 
  doc, 
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';

// Backup all collections
export const createBackup = async () => {
  try {
    const backup = {
      timestamp: new Date().toISOString(),
      version: '1.0.0',
      collections: {}
    };

    // Get all screens
    const screensSnapshot = await getDocs(collection(db, 'screens'));
    const screens = {};
    screensSnapshot.forEach((doc) => {
      screens[doc.id] = { id: doc.id, ...doc.data() };
    });
    backup.collections.screens = screens;

    // Get all checkpoints
    const checkpointsSnapshot = await getDocs(collection(db, 'checkpoints'));
    const checkpoints = {};
    checkpointsSnapshot.forEach((doc) => {
      checkpoints[doc.id] = { id: doc.id, ...doc.data() };
    });
    backup.collections.checkpoints = checkpoints;

    // Get all users (excluding sensitive data)
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users = {};
    usersSnapshot.forEach((doc) => {
      const userData = doc.data();
      // Remove sensitive fields and keep only essential structure
      const sanitizedUser = {
        id: doc.id,
        shortcode: userData.shortcode,
        currentScreen: userData.currentScreen,
        completedScreens: userData.completedScreens || [],
        startTime: userData.startTime,
        lastActive: userData.lastActive,
        completedFunnel: userData.completedFunnel,
        // Keep response structure but remove personal data
        responses: userData.responses ? Object.keys(userData.responses).reduce((acc, key) => {
          const response = userData.responses[key];
          acc[key] = {
            checkpointKey: response.checkpointKey,
            type: response.type,
            selectedOption: response.selectedOption,
            selectedOptionText: response.selectedOptionText,
            timestamp: response.timestamp
          };
          return acc;
        }, {}) : {},
        // Keep journal entries count but not content
        journalEntriesCount: userData.journalEntries ? userData.journalEntries.length : 0,
        // Keep calculated metrics structure but not values
        hasCalculatedMetrics: !!userData.calculatedMetrics
      };
      users[doc.id] = sanitizedUser;
    });
    backup.collections.users = users;

    // Get collection metadata
    backup.metadata = {
      screensCount: Object.keys(screens).length,
      checkpointsCount: Object.keys(checkpoints).length,
      usersCount: Object.keys(users).length,
      totalSize: JSON.stringify(backup).length
    };

    return backup;
  } catch (error) {
    console.error('Error creating backup:', error);
    throw new Error(`Failed to create backup: ${error.message}`);
  }
};

// Download backup as JSON file
export const downloadBackup = (backup, filename = null) => {
  try {
    const dataStr = JSON.stringify(backup, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const defaultFilename = `optimalpace-backup-${new Date().toISOString().split('T')[0]}.json`;
    const downloadFilename = filename || defaultFilename;
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = downloadFilename;
    link.click();
    
    URL.revokeObjectURL(link.href);
    return true;
  } catch (error) {
    console.error('Error downloading backup:', error);
    throw new Error(`Failed to download backup: ${error.message}`);
  }
};

// Upload and parse backup file
export const uploadBackup = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const backup = JSON.parse(event.target.result);
        
        // Validate backup structure
        if (!backup.timestamp || !backup.version || !backup.collections) {
          throw new Error('Invalid backup file format');
        }
        
        if (!backup.collections.screens || !backup.collections.checkpoints) {
          throw new Error('Backup missing required collections');
        }
        
        resolve(backup);
      } catch (error) {
        reject(new Error(`Failed to parse backup file: ${error.message}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read backup file'));
    };
    
    reader.readAsText(file);
  });
};

// Restore backup to Firebase
export const restoreBackup = async (backup, options = {}) => {
  const {
    clearExisting = false,
    onProgress = null,
    dryRun = false
  } = options;

  try {
    if (dryRun) {
      // Just validate the backup without making changes
      return {
        success: true,
        message: 'Dry run completed successfully',
        summary: {
          screensToRestore: Object.keys(backup.collections.screens).length,
          checkpointsToRestore: Object.keys(backup.collections.checkpoints).length,
          usersToRestore: Object.keys(backup.collections.users || {}).length
        }
      };
    }

    const batch = writeBatch(db);
    let operationsCount = 0;

    // Clear existing data if requested
    if (clearExisting) {
      if (onProgress) onProgress('Clearing existing data...', 0);
      
      // Clear screens
      const existingScreens = await getDocs(collection(db, 'screens'));
      existingScreens.forEach((doc) => {
        batch.delete(doc.ref);
        operationsCount++;
      });

      // Clear checkpoints
      const existingCheckpoints = await getDocs(collection(db, 'checkpoints'));
      existingCheckpoints.forEach((doc) => {
        batch.delete(doc.ref);
        operationsCount++;
      });

      // Clear users (optional - usually you don't want to clear user data)
      if (options.clearUsers) {
        const existingUsers = await getDocs(collection(db, 'users'));
        existingUsers.forEach((doc) => {
          batch.delete(doc.ref);
          operationsCount++;
        });
      }
    }

    // Restore screens
    if (onProgress) onProgress('Restoring screens...', 25);
    Object.values(backup.collections.screens).forEach((screen) => {
      const { id, ...screenData } = screen;
      const screenRef = doc(db, 'screens', id);
      batch.set(screenRef, screenData);
      operationsCount++;
    });

    // Restore checkpoints
    if (onProgress) onProgress('Restoring checkpoints...', 50);
    Object.values(backup.collections.checkpoints).forEach((checkpoint) => {
      const { id, ...checkpointData } = checkpoint;
      const checkpointRef = doc(db, 'checkpoints', id);
      batch.set(checkpointRef, checkpointData);
      operationsCount++;
    });

    // Restore users (if requested and backup contains user data)
    if (backup.collections.users && Object.keys(backup.collections.users).length > 0) {
      if (onProgress) onProgress('Restoring users...', 75);
      Object.values(backup.collections.users).forEach((user) => {
        const { id, ...userData } = user;
        const userRef = doc(db, 'users', id);
        batch.set(userRef, userData);
        operationsCount++;
      });
    }

    if (onProgress) onProgress('Committing changes...', 90);

    // Commit all changes
    await batch.commit();

    if (onProgress) onProgress('Restore completed!', 100);

    return {
      success: true,
      message: 'Backup restored successfully',
      summary: {
        operationsPerformed: operationsCount,
        screensRestored: Object.keys(backup.collections.screens).length,
        checkpointsRestored: Object.keys(backup.collections.checkpoints).length,
        usersRestored: backup.collections.users ? Object.keys(backup.collections.users).length : 0,
        timestamp: new Date().toISOString()
      }
    };

  } catch (error) {
    console.error('Error restoring backup:', error);
    throw new Error(`Failed to restore backup: ${error.message}`);
  }
};

// Get backup info without downloading
export const getBackupInfo = async () => {
  try {
    const backup = await createBackup();
    
    return {
      timestamp: backup.timestamp,
      version: backup.version,
      metadata: backup.metadata,
      estimatedSize: JSON.stringify(backup).length
    };
  } catch (error) {
    console.error('Error getting backup info:', error);
    throw new Error(`Failed to get backup info: ${error.message}`);
  }
};

// Get backup info without creating full backup (faster)
export const getBackupInfoFast = async () => {
  try {
    // Get collection counts without full data
    const [screensSnapshot, checkpointsSnapshot, usersSnapshot] = await Promise.all([
      getDocs(collection(db, 'screens')),
      getDocs(collection(db, 'checkpoints')),
      getDocs(collection(db, 'users'))
    ]);

    const screensCount = screensSnapshot.size;
    const checkpointsCount = checkpointsSnapshot.size;
    const usersCount = usersSnapshot.size;

    return {
      timestamp: new Date().toISOString(),
      version: '1.0.0',
      metadata: {
        screensCount,
        checkpointsCount,
        usersCount,
        totalSize: 'Calculating...'
      },
      estimatedSize: 'Calculating...'
    };
  } catch (error) {
    console.error('Error getting backup info:', error);
    throw new Error(`Failed to get backup info: ${error.message}`);
  }
};

// Validate backup file before restoration
export const validateBackup = (backup) => {
  const errors = [];
  const warnings = [];

  // Check required fields
  if (!backup.timestamp) errors.push('Missing timestamp');
  if (!backup.version) errors.push('Missing version');
  if (!backup.collections) errors.push('Missing collections');

  // Check collections
  if (!backup.collections.screens) {
    errors.push('Missing screens collection');
  } else {
    const screens = backup.collections.screens;
    if (Object.keys(screens).length === 0) {
      warnings.push('Screens collection is empty');
    }
  }

  if (!backup.collections.checkpoints) {
    errors.push('Missing checkpoints collection');
  } else {
    const checkpoints = backup.collections.checkpoints;
    if (Object.keys(checkpoints).length === 0) {
      warnings.push('Checkpoints collection is empty');
    }
  }

  // Check for potential issues
  if (backup.collections.users) {
    const users = backup.collections.users;
    if (Object.keys(users).length > 1000) {
      warnings.push('Large number of users detected - restoration may take time');
    }
  }

  // Check backup age
  if (backup.timestamp) {
    const backupDate = new Date(backup.timestamp);
    const now = new Date();
    const daysOld = (now - backupDate) / (1000 * 60 * 60 * 24);
    
    if (daysOld > 30) {
      warnings.push('Backup is over 30 days old - data may be outdated');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    summary: {
      screensCount: backup.collections?.screens ? Object.keys(backup.collections.screens).length : 0,
      checkpointsCount: backup.collections?.checkpoints ? Object.keys(backup.collections.checkpoints).length : 0,
      usersCount: backup.collections?.users ? Object.keys(backup.collections.users).length : 0
    }
  };
};
